// Tunable game constants. Centralize them so balancing changes touch one file.

export const XP = {
  // Base XP for completing a game.
  WIN_BASE: 200,

  // Move efficiency: dominant variable component. Fewer moves → more XP.
  // Klondike minimum is ~70-80 moves for an "ideal" win; we cap the bonus.
  MOVE_BONUS_MAX: 200,
  MOVE_BONUS_REF: 80, // moves at or below this give max move bonus
  MOVE_BONUS_FALLOFF_PER_MOVE: 2, // XP lost per move above ref

  // Time bonus: small, threshold-based to be friendly to slow devices.
  TIME_BONUS_THRESHOLDS_SEC: [120, 240, 480], // ≤2min, ≤4min, ≤8min
  TIME_BONUS_VALUES: [60, 30, 10],

  // Penalties.
  UNDO_PENALTY: 3,
  HINT_PENALTY: 10,

  // First-of-day bonus multiplier (requirement).
  FIRST_DAILY_MULTIPLIER: 2,

  // Streak bonus: small additive, capped.
  STREAK_BONUS_PER_DAY: 5,
  STREAK_BONUS_MAX: 100,

  // 3-card draw multiplier — initially 1.0, configurable later.
  MULTIPLIER_3CARD: 1.0,
} as const;

export const LEVEL = {
  CAP: 999,
  // required(level) for going from `level` to `level+1`.
  // Hybrid: linear-ish early, exponential afterward.
  // Tune later from telemetry.
  BASE: 100,
  MULTIPLIER: 1.04,
  LINEAR_BREAK: 20, // levels 1..LINEAR_BREAK use shallower curve
  LINEAR_STEP: 30,
} as const;

export const JOKERS = {
  // Temporary reward rule. Keep all awarding logic behind helpers so this can
  // become "every 10 rounds", roulette, server campaigns, etc. without touching UI.
  LEVEL_UP_REWARD: 2,
} as const;

// Required XP to advance from `level` to `level+1`.
export function xpRequiredForLevel(level: number): number {
  if (level >= LEVEL.CAP) return Infinity;
  if (level <= LEVEL.LINEAR_BREAK) {
    return Math.round(LEVEL.BASE + LEVEL.LINEAR_STEP * (level - 1));
  }
  const overflow = level - LEVEL.LINEAR_BREAK;
  const linearTopValue = LEVEL.BASE + LEVEL.LINEAR_STEP * (LEVEL.LINEAR_BREAK - 1);
  return Math.round(linearTopValue * Math.pow(LEVEL.MULTIPLIER, overflow));
}

// Compute level + remaining XP within the level from cumulative XP.
export function levelFromCumulativeXp(cumulative: number): {
  level: number;
  xpInLevel: number;
  xpForNext: number;
} {
  let level = 1;
  let remaining = cumulative;
  while (level < LEVEL.CAP) {
    const need = xpRequiredForLevel(level);
    if (remaining < need) return { level, xpInLevel: remaining, xpForNext: need };
    remaining -= need;
    level++;
  }
  return { level: LEVEL.CAP, xpInLevel: 0, xpForNext: Infinity };
}

export const RULES = {
  STOCK_CYCLES_LIMIT: Infinity, // no limit; some variants cap at 1 or 3
  AUTOCOMPLETE_ENABLED: true,
  SOLVABLE_ONLY: true,
  // Solver budget per attempt. Most solvable 1-card deals converge in <30k states;
  // tightening this keeps generation responsive while still catching obviously-stuck deals.
  SOLVER_MAX_STATES_ONE: 250_000,
  SOLVER_MAX_STATES_THREE: 400_000,
  SOLVER_MAX_DEPTH_ONE: 800,
  SOLVER_MAX_DEPTH_THREE: 1_000,
} as const;
